import { BAYAN_CITY_ID_BY_NAME, BAYAN_CITY_NAME_BY_ID } from './bayan.cities';

export class BayanCityUnmappedError extends Error {
  constructor(
    readonly bayanName: string | undefined,
    readonly regionCustomCode?: string,
  ) {
    super(
      bayanName
        ? `Bayan has no city id for "${bayanName}"`
        : `Region ${regionCustomCode ?? '(unknown)'} has no metadata.bayanName`,
    );
    this.name = 'BayanCityUnmappedError';
  }
}

/**
 * Normalize a Bayan city name for lookup.
 *
 * The appendix keys are upper-case with hyphens (`AL-DWADMY`), but Excel
 * imports and manual edits drift on case and whitespace. Hyphen vs space is
 * NOT normalized away — `AL-KHARJ` and `AL KHARJ` are genuinely different
 * keys in ELM's table, so collapsing them would risk a wrong match.
 */
const normalizeCityName = (name: string): string => name.trim().replace(/\s+/g, ' ').toUpperCase();

const CITY_ID_BY_NORMALIZED_NAME: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(
    Object.entries(BAYAN_CITY_ID_BY_NAME).map(([name, id]) => [normalizeCityName(name), id]),
  ),
);

/**
 * Google / common spellings that Bayan's appendix writes differently.
 *
 * Hero-side lookup only — the id that goes on the wire is still the
 * appendix's (`AL-GBEIL` → 60). Do not collapse hyphen vs space in
 * general; these are explicit known pairs.
 */
const BAYAN_CITY_NAME_SYNONYMS: Readonly<Record<string, string>> = Object.freeze({
  [normalizeCityName('Al Jubail')]: 'AL-GBEIL',
  [normalizeCityName('Al-Jubail')]: 'AL-GBEIL',
  [normalizeCityName('Jubail')]: 'AL-GBEIL',
  [normalizeCityName('Al Jubayl')]: 'AL-GBEIL',
  [normalizeCityName('Al Gbeil')]: 'AL-GBEIL',
  [normalizeCityName('الجبيل')]: 'AL-GBEIL',
});

/**
 * Resolve a Bayan `cityId` from a Region's `metadata.bayanName`.
 *
 * Returns `undefined` rather than throwing so callers can aggregate several
 * unmapped cities into one validation message. Use
 * {@link requireBayanCityId} when a single missing city should abort.
 */
export const resolveBayanCityId = (bayanName: string | undefined): number | undefined => {
  if (!bayanName?.trim()) return undefined;
  const normalized = normalizeCityName(bayanName);
  const canonical = BAYAN_CITY_NAME_SYNONYMS[normalized] ?? normalized;
  return CITY_ID_BY_NORMALIZED_NAME[normalizeCityName(canonical)];
};

/**
 * Resolve a Bayan city id from a Google Places / order-address city snapshot.
 *
 * Orders store `address.city.customCode` from `makeCustomCode(long_name)`,
 * which for الجبيل is `al_jubail` or `الجبيل` — not the dictionary slug
 * `al_gbeil`. Trying the display names and the de-sluged customCode is what
 * lets issuance find appendix id 60 without a Region ref.
 */
export const resolveBayanCityIdFromPlace = (place?: {
  customCode?: string;
  name?: string;
  long_name?: string;
}): number | undefined => {
  if (!place) return undefined;
  const slugAsName = place.customCode?.replace(/_/g, ' ');
  return (
    resolveBayanCityId(place.long_name) ??
    resolveBayanCityId(place.name) ??
    resolveBayanCityId(slugAsName)
  );
};

/**
 * Same as {@link resolveBayanCityId} but throws {@link BayanCityUnmappedError}.
 *
 * Bayan requires `cityId` whenever `countryCode` is `SA`, and a waybill with
 * the wrong city is a compliance problem, so there is no "best guess"
 * fallback here by design.
 */
export const requireBayanCityId = (args: {
  bayanName: string | undefined;
  regionCustomCode?: string;
}): number => {
  const id = resolveBayanCityId(args.bayanName);
  if (!id) throw new BayanCityUnmappedError(args.bayanName, args.regionCustomCode);
  return id;
};

/** Bayan's own English name for a city id — for rendering echoed trip data. */
export const getBayanCityName = (cityId: number): string | undefined =>
  BAYAN_CITY_NAME_BY_ID[cityId];
